import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, Loader2, Wrench } from 'lucide-react';
import type { ToolCallDto } from '@paladin-monorepo/common';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { formatUpdatedFields, getToolCallDisplayInfo, getVisibleToolCalls } from './llm-chat-tool-display';

export interface LlmChatToolSequenceItem {
  key: string;
  label: string;
  context?: string | null;
  status?: 'running' | 'finished';
}

interface LlmChatToolSequenceProps {
  items: LlmChatToolSequenceItem[];
}

function ToolSequenceIcon({ status }: { status?: 'running' | 'finished' }) {
  if (status === 'running') {
    return <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />;
  }
  if (status === 'finished') {
    return <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
  }
  return <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
}

function getToolLabelClassName(status?: 'running' | 'finished'): string | undefined {
  if (status === 'finished') return 'text-muted-foreground';
  if (status === 'running') return undefined;
  return 'font-medium';
}

function ToolSequenceRows({ items }: { items: LlmChatToolSequenceItem[] }) {
  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.key} className="flex items-start gap-2 text-sm">
          <ToolSequenceIcon status={item.status} />
          <div className="min-w-0">
            <p className={getToolLabelClassName(item.status)}>{item.label}</p>
            {item.context && (
              <p className="mt-0.5 text-xs text-muted-foreground">{item.context}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function LlmChatToolSequence({ items }: LlmChatToolSequenceProps) {
  const { t } = useTranslation();

  if (items.length === 0) return null;

  if (items.length === 1) {
    return <ToolSequenceRows items={items} />;
  }

  const hasRunning = items.some((item) => item.status === 'running');

  return (
    <Collapsible className="group/tool-sequence">
      <CollapsibleTrigger
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 rounded-md py-0.5 text-left text-sm text-muted-foreground',
          'hover:text-foreground',
          'outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        {hasRunning ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
        ) : (
          <Wrench className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className="min-w-0 flex-1 truncate">
          {t(hasRunning ? 'llmChat.toolCalls.usingTools' : 'llmChat.toolCalls.usedTools', {
            count: items.length,
          })}
        </span>
        <ChevronRight className="h-3.5 w-3.5 shrink-0 transition-transform group-data-[state=open]/tool-sequence:rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <ToolSequenceRows items={items} />
      </CollapsibleContent>
    </Collapsible>
  );
}

interface LlmChatToolCallsSummaryProps {
  toolCalls: ToolCallDto[];
}

export function LlmChatToolCallsSummary({ toolCalls }: LlmChatToolCallsSummaryProps) {
  const { t } = useTranslation();
  const items: LlmChatToolSequenceItem[] = getVisibleToolCalls(toolCalls).map((toolCall, index) => {
    const info = getToolCallDisplayInfo(toolCall);
    const context = info.contextKey && info.contextValues
      ? info.contextKey === 'llmChat.toolCalls.context.updatedFields' && info.contextValues.fields
        ? t('llmChat.toolCalls.context.updatedFields', {
            fields: formatUpdatedFields(info.contextValues.fields, t),
          })
        : t(info.contextKey, info.contextValues)
      : null;

    return {
      key: `${toolCall.id}-${index}`,
      label: t(info.nameKey, { defaultValue: toolCall.name }),
      context,
    };
  });

  return <LlmChatToolSequence items={items} />;
}
